/**
 * THE REFERENCE LOOKUP (`API-CONTRACT.md` §2 "Rows carry ids only"). Sites, modes and carriers are
 * small client-loaded lists, so a `siteId` / `modeId` / `carrierId` on any row resolves to its
 * business code through ONE shared read each — the same query key the register itself uses.
 *
 * A uuid is never shown. Until `ready`, a cell renders a short skeleton instead.
 *
 * Products are 22,257 rows and are NEVER loaded whole: `useProduct(id)` resolves one on demand.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { api } from "../api";
import type { Carrier, Mode, Product, Site } from "../client";
import { queryKeys } from "../queryKeys";
import { clientListOptions } from "./useClientRegister";
import { useBootstrap } from "./useBootstrap";

export interface SiteReference {
  siteCode: string;
  name: string;
}

export interface ReferenceLookup {
  /** `{ siteCode, name }`, or `undefined` while the list is still loading. */
  site: (id: string | null | undefined) => SiteReference | undefined;
  /** The complete cached site row, used where capacity fields are required. Adds no request. */
  siteRow: (id: string | null | undefined) => Site | undefined;
  /** The mode's `code`. */
  mode: (id: string | null | undefined) => string | undefined;
  /** The carrier's `code`. */
  carrier: (id: string | null | undefined) => string | undefined;
  /** A site's id from its `siteCode` — the drift route's filters travel as codes. */
  siteIdByCode: (code: string | null | undefined) => string | undefined;
  /** All three lists have answered. */
  ready: boolean;
}

export function useReferenceLookup(): ReferenceLookup {
  const { data: boot } = useBootstrap();
  const tenantKey = boot?.tenant.key ?? null;

  const sites = useQuery(clientListOptions<Site>(tenantKey, "sites", "listSites"));
  const modes = useQuery(clientListOptions<Mode>(tenantKey, "modes", "listModes"));
  const carriers = useQuery(clientListOptions<Carrier>(tenantKey, "carriers", "listCarriers"));

  const siteIndex = useMemo(() => {
    const map = new Map<string, SiteReference>();
    for (const row of sites.data?.rows ?? [])
      map.set(row.id, { siteCode: row.siteCode, name: row.name });
    return map;
  }, [sites.data]);

  const siteRowIndex = useMemo(() => {
    const map = new Map<string, Site>();
    for (const row of sites.data?.rows ?? []) map.set(row.id, row);
    return map;
  }, [sites.data]);

  const siteCodeIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of sites.data?.rows ?? []) map.set(row.siteCode, row.id);
    return map;
  }, [sites.data]);

  const modeIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of modes.data?.rows ?? []) map.set(row.id, row.code);
    return map;
  }, [modes.data]);

  const carrierIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of carriers.data?.rows ?? []) map.set(row.id, row.code);
    return map;
  }, [carriers.data]);

  const ready = Boolean(sites.data && modes.data && carriers.data);

  return useMemo(
    () => ({
      ready,
      site: (id) => (id ? siteIndex.get(id) : undefined),
      siteRow: (id) => (id ? siteRowIndex.get(id) : undefined),
      siteIdByCode: (code) => (code ? siteCodeIndex.get(code) : undefined),
      mode: (id) => (id ? modeIndex.get(id) : undefined),
      carrier: (id) => (id ? carrierIndex.get(id) : undefined),
    }),
    [ready, siteIndex, siteRowIndex, siteCodeIndex, modeIndex, carrierIndex],
  );
}

/** One product by id, cached per id. The products list is never loaded whole. */
export function useProduct(id: string | null | undefined) {
  const { data: boot } = useBootstrap();
  const tenantKey = boot?.tenant.key ?? null;

  return useQuery({
    queryKey: queryKeys.products.detail(tenantKey ?? "_", id ?? "_"),
    enabled: tenantKey !== null && Boolean(id),
    queryFn: () =>
      api.call("getProduct", { path: { product_id: id as string } }) as Promise<Product>,
  });
}
